import { Button, Col, Form, Row } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RouteChildrenProps } from "react-router";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { FormChangeDebounceTime, ReportStatesEnum, VALIDATION_MESSAGE_CONFIG } from "../../../../../../constants/constants";
import { ROUTE_PATHS } from "../../../../../../constants/url-config";
import { MessageType } from "../../../../../../models/common/response-message";
import { FormSectionProps } from "../../../../../../models/form-section.model";
import { ResourcesModel } from "../../../../../../models/resources.model";
import { ControlEffectivenessesModel, ScenarioMitigationModel } from "../../../../../../models/scenario-mitigation.model";
import { setActions } from "../../../../../../redux/actions/header.action";
import { ClientService } from "../../../../../../services/client.service";
import { ERMService } from "../../../../../../services/erm.service";
import i18n from "../../../../../../utils/i18n";
import { ROLE_GUARD } from "../../../../../../utils/role-guard";
import UTIL from "../../../../../../utils/utils";
import { VALIDATION_UTIL } from "../../../../../../utils/validation.utils";
import AppAnchor from "../../../../../atoms/anchor";
import { InfoCircleIcon } from "../../../../../atoms/icon";
import { showMessage } from "../../../../../atoms/notification/notification";
import { AppSelect } from "../../../../../atoms/select";
import ControlEffectivenessModal from "./control-effectiveness-modal";
import ControlEffectiveness from "./control-effectiveness-section";
import ResidualRickSection from "./residual-risk-section";
import RiskSeverityImpact from "./risk-severity-impact-section";
import "./scenario-mitigation-form.scss";
import SourceSection from "./source-section";

export interface MitigationSectionProps extends FormSectionProps<ScenarioMitigationModel> {
    showModal?: (item?: ControlEffectivenessesModel) => void;
    deleteControlEffectivenesses?: (id: number) => void;
    gogoAppraisalTool?: (state?: ReportStatesEnum) => void;
}

const formChanged = new Subject<ScenarioMitigationModel>();

interface Props extends RouteChildrenProps<{ scenarioId: string }> { }
const ScenarioMitigationForm = (props: Props) => {

    const { history, match } = props;
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const scenatioId = React.useRef(0);
    const id = useRef<number | undefined>(undefined);
    const [data, setData] = useState<ScenarioMitigationModel>({});
    const [resources, setResources] = useState<ResourcesModel>({});
    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    const [modalData, setModalData] = useState<ControlEffectivenessesModel | undefined>({});
    const valuesChanged = useRef<ScenarioMitigationModel>({});
    const [modalRequiredFields, setModalRequiredFields] = useState<string[]>([]);
    const dispatch = useDispatch();
    const isOwner = useRef<boolean>(false);

    const sections = useRef([
        {
            title: "",
            id: 2,
            is_completed: false,
            sectionComponent: SourceSection
        },
        {
            title: t("ScenarioMitigation.lbRiskSeverityImpact"),
            id: 3,
            is_completed: false,
            sectionComponent: RiskSeverityImpact
        },
        {
            title: t("ScenarioMitigation.ttControlEffectiveness"),
            id: 4,
            is_completed: false,
            sectionComponent: ControlEffectiveness
        },
        {
            title: i18n.t("Residual Risk"),
            id: 5,
            is_completed: false,
            sectionComponent: ResidualRickSection
        }
    ])

    const next = () => {
        form.submit();
    }

    const goBack = () => {
        history.goBack();
    }

    const autoSave = () => {
        if (valuesChanged.current) {
            if (valuesChanged.current.review_dates) {
                valuesChanged.current.review_dates = form.getFieldValue("review_dates");
            }

            if (valuesChanged.current.internal || valuesChanged.current.external) {
                valuesChanged.current.internal = form.getFieldValue("internal");
                valuesChanged.current.external = form.getFieldValue("external");
            }

            const value = UTIL.prepareMitigationFormForSubmit(valuesChanged.current);
            valuesChanged.current = {}; // reset changed values
            ERMService.saveDraftMitigationAsync(scenatioId.current, value, false).then(res => {
                UTIL.setSectionIsCompleted(res.data._section_statuses, sections.current);
                setData(res.data);
            });
        }
    }

    const saveToLoadData = () => {
        ERMService.saveDraftMitigationAsync(scenatioId.current, {}, true).then(res => {
            setData(res.data);
        })
    }

    const renderActions = () => (
        <>
            <Button onClick={goBack} htmlType="button" type="default" shape="round" block >{t("ScenarioMitigation.btnGoback")}</Button>
            <Button onClick={next} htmlType="button" type="primary" shape="round" block >{t("ScenarioMitigation.btnSubmit")}</Button>
        </>);

    const addOrUpdateControlEffectivenesses = (data: ControlEffectivenessesModel) => {

        if (!id.current) {
            return;
        }

        if (data.weight_percent) {
            data.weight = data.weight_percent / 100;
        }


        if (data.id) {
            ERMService.updateControlEffectivenessesAsync(id.current, data).then(res => {
                setModalVisible(false);
                saveToLoadData();
            });
        } else {
            ERMService.createControlEffectivenessesAsync(id.current, data).then(res => {
                setModalVisible(false);
                saveToLoadData();
            });
        }
    }

    const deleteControlEffectivenesses = (id: number) => {
        ERMService.deleteControlEffectivenessesAsync(scenatioId.current, id).then(res => {
            saveToLoadData();
        });
    }

    const showModal = (item?: ControlEffectivenessesModel) => {
        if (item) {

            if (item.weight) {
                item.weight_percent = item.weight * 100;
            }

            setModalData(item);
        } else {
            setModalData(undefined);
        }
        setModalVisible(true);
    }

    const formSubmit = (values: ScenarioMitigationModel) => {
        const value = UTIL.prepareMitigationFormForSubmit(values);
        ERMService.updateMitigationAsync(scenatioId.current, value).then(res => {
            showMessage({ message: t("Global.lbSavedSuccessfully"), type: MessageType.success });
            goBack();
        });
    }

    const gotoAppraisalTool = (state?: ReportStatesEnum) => {
        if(isOwner.current && UTIL.checkCanEditByState(state)){
            history.push(`${ROUTE_PATHS.ScenarioAppraisal}/${scenatioId.current}`);
        } else {
            history.push(`${ROUTE_PATHS.ScenarioAppraisalView}/${scenatioId.current}`);
        }
    }

    const formChange = (changedValues: ScenarioMitigationModel) => {
        valuesChanged.current = { ...valuesChanged.current, ...changedValues }
        formChanged.next(valuesChanged.current);
    }

    const initFormData = (val: ScenarioMitigationModel) => {
        const newData: ScenarioMitigationModel = UTIL.convertDataMitigationForm(val);
        isOwner.current = ROLE_GUARD.isCurrentUser(newData.created_by);
        if (newData.further_risk_reduction_required != null) {
            newData.further_risk_reduction_required = newData.further_risk_reduction_required.toString();
        }
        id.current = newData.id;
        form.setFieldsValue(newData);
        sections.current[0].title = `${t("ScenarioMitigation.lbSourceOf")} ${newData.loss_scenario?.name}`;
        UTIL.setSectionIsCompleted(val._section_statuses, sections.current);
        setData(newData);
        setRequiredFields(UTIL.getAllRequiredFields(newData._sections));
        const controlSection = newData._sections?.find(p => p.field_conditions && p.field_conditions?.control_effectivenesses);
        if (controlSection) {
            setModalRequiredFields(controlSection.field_conditions.control_effectivenesses.required_fields);
        }
    }

    useEffect(() => {

        const formChangedSub = formChanged
            .pipe(debounceTime(FormChangeDebounceTime))
            .subscribe((value) => {
                if (value) {
                    autoSave();
                }
            });

        scenatioId.current = Number(match?.params.scenarioId);

        ERMService.newMitigationAsync(scenatioId.current).then(res => {
            initFormData(res.data);
        });

        const actions = renderActions()
        dispatch(setActions(actions));

        ClientService.getResourcesAsync().then(res => {
            setResources(res.data);
        })

        return () => {
            dispatch(setActions(undefined));
            formChangedSub.unsubscribe();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])



    return (
        <div className='scenario-mitigation-form-wrapper'>
            <Row gutter={20}>
                <Col xs={0} sm={5}>
                    <AppAnchor sections={sections.current} />
                </Col>
                <Col xs={0} sm={14}>
                    <Form
                        className="main-form"
                        form={form}
                        layout='vertical'
                        validateMessages={VALIDATION_MESSAGE_CONFIG}
                        onFinish={(values) => formSubmit(values)}
                        onValuesChange={formChange}
                        scrollToFirstError>

                        <div className="form-header">
                            <div className="title">
                                {data.loss_scenario?.name}
                            </div>

                        </div>

                        <div className="form-section">
                            <div className="section-wrapper">
                                <Row gutter={[16, 32]}>
                                    <Col span={24}>
                                        <AppSelect
                                            list={resources.scenario_mitigation_resources?.owners}
                                            label={t("ScenarioMitigation.lbScenarioOwner")}
                                            name="owner_id"
                                            tooltip={{ title: t("ScenarioMitigation.lbScenarioOwner"), icon: <InfoCircleIcon /> }}
                                            placeholder={t("Global.plPleaseSelect")}
                                            rules={VALIDATION_UTIL.getRulesValidationField("owner_id", requiredFields)} />
                                    </Col>
                                </Row>
                                <Row gutter={[16, 32]}>
                                    <Col span={24}>
                                        <AppSelect
                                            list={resources.scenario_mitigation_resources?.classifications}
                                            label={t("ScenarioMitigation.lbClassification")}
                                            name="classification_id"
                                            tooltip={{ title: t("ScenarioMitigation.lbClassification"), icon: <InfoCircleIcon /> }}
                                            placeholder={t("Global.plPleaseSelect")}
                                            rules={VALIDATION_UTIL.getRulesValidationField("classification_id", requiredFields)} />
                                    </Col>
                                </Row>
                            </div>
                        </div>


                        {sections.current.map((item, index) => (
                            <div id={`section${item.id}`} key={index} className='form-section'>
                                <p className='section-title'>
                                    {item.title}
                                </p>
                                <item.sectionComponent
                                    showModal={showModal}
                                    data={data}
                                    requiredFields={requiredFields}
                                    resources={resources}
                                    deleteControlEffectivenesses={deleteControlEffectivenesses}
                                    gogoAppraisalTool={gotoAppraisalTool} />
                            </div>
                        ))}

                        <Row gutter={12}>
                            <Col span={24} className="form-action">
                                {
                                    renderActions()
                                }
                            </Col>
                        </Row>

                    </Form>
                </Col>
                <Col xs={0} sm={5}>
                </Col>
            </Row>

            <ControlEffectivenessModal
                requiredFields={modalRequiredFields}
                data={modalData}
                visible={modalVisible}
                onOK={(data) => addOrUpdateControlEffectivenesses(data)}
                onCancle={() => setModalVisible(false)}
                resources={resources}
            />
        </div>

    )
}

export default ScenarioMitigationForm;
